import React, { useState, useMemo, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency, getDateRange, getVehicleBillTotal } from '../utils/helpers';
import { isStageEnabled } from '../utils/enabledStages';

const Finance = ({ onOpenVehicleDetail }) => {
  const { vehicles, invoices, garageInfo } = useApp();
  const [period, setPeriod] = useState('week');
  const [category, setCategory] = useState('all'); // 'all', 'garage', 'sales'

  // Get date range for selected period
  const { startDate, endDate } = useMemo(() => {
    return getDateRange(period);
  }, [period]);

  // Helper to check if a date is within the period
  const isInPeriod = useCallback((dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date >= startDate && date <= endDate;
  }, [startDate, endDate]);

  // Exclude archived vehicles from financial calculations
  const activeVehicles = useMemo(
    () => vehicles.filter((v) => v.status !== 'Archive' && !v.deletedAt && !v.collected),
    [vehicles]
  );

  // Calculate financial data for Garage (Workshop/MOT/Bodyshop/Tyres)
  const garageData = useMemo(() => {
    const garageVehicles = activeVehicles.filter(v =>
      ['Workshop', 'MOT', 'Bodyshop', 'Tyres'].includes(v.status) && isStageEnabled(garageInfo, v.status)
    );

    // Get invoices for garage vehicles
    const garageInvoices = invoices.filter(inv => {
      const vehicle = garageVehicles.find(v => v.reg === inv.reg);
      return vehicle && isInPeriod(inv.date);
    });

    // Calculate totals from invoices (more accurate)
    const totalRevenue = garageInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const totalSubtotal = garageInvoices.reduce((sum, inv) => sum + (inv.subtotal || 0), 0);
    const totalVAT = garageInvoices.reduce((sum, inv) => sum + (inv.vat || 0), 0);
    const invoiceCount = garageInvoices.length;

    // Also calculate from vehicles (for vehicles without invoices)
    const vehiclesWithoutInvoices = garageVehicles.filter(v => {
      const hasInvoice = garageInvoices.some(inv => inv.reg === v.reg);
      return !hasInvoice && isInPeriod(v.collectionDate || v.dropOffDate);
    });

    const vehiclesRevenue = vehiclesWithoutInvoices.reduce((sum, veh) => sum + getVehicleBillTotal(veh), 0);

    const totalRevenueWithVehicles = totalRevenue + vehiclesRevenue;
    const totalVehicles = garageVehicles.filter(v => 
      isInPeriod(v.collectionDate || v.dropOffDate)
    ).length;

    // Breakdown by status
    const workshopVehicles = garageVehicles.filter(v => v.status === 'Workshop' && isInPeriod(v.collectionDate || v.dropOffDate));
    const motVehicles = garageVehicles.filter(v => v.status === 'MOT' && isInPeriod(v.collectionDate || v.dropOffDate));
    const bodyshopVehicles = garageVehicles.filter(v => v.status === 'Bodyshop' && isInPeriod(v.collectionDate || v.dropOffDate));
    const tyresVehicles = garageVehicles.filter(v => v.status === 'Tyres' && isInPeriod(v.collectionDate || v.dropOffDate));

    const workshopRevenue = workshopVehicles.reduce((sum, veh) => {
      const invoice = garageInvoices.find(inv => inv.reg === veh.reg);
      if (invoice) return sum + (invoice.total || 0);
      return sum + getVehicleBillTotal(veh);
    }, 0);

    const motRevenue = motVehicles.reduce((sum, veh) => {
      const invoice = garageInvoices.find(inv => inv.reg === veh.reg);
      if (invoice) return sum + (invoice.total || 0);
      return sum + getVehicleBillTotal(veh);
    }, 0);

    const bodyshopRevenue = bodyshopVehicles.reduce((sum, veh) => {
      const invoice = garageInvoices.find(inv => inv.reg === veh.reg);
      if (invoice) return sum + (invoice.total || 0);
      return sum + getVehicleBillTotal(veh);
    }, 0);

    const tyresRevenue = tyresVehicles.reduce((sum, veh) => {
      const invoice = garageInvoices.find(inv => inv.reg === veh.reg);
      if (invoice) return sum + (invoice.total || 0);
      return sum + getVehicleBillTotal(veh);
    }, 0);

    return {
      totalRevenue: totalRevenueWithVehicles,
      totalSubtotal: totalSubtotal + vehiclesWithoutInvoices.reduce((sum, veh) => sum + (veh.cost || 0), 0),
      totalVAT: totalVAT + vehiclesWithoutInvoices.reduce((sum, veh) => {
        const sub = veh.cost || 0;
        const vatEnabled = veh.status === 'MOT' ? false : (veh.includeVat !== undefined ? veh.includeVat : true);
        return sum + (vatEnabled ? sub * 0.2 : 0);
      }, 0),
      invoiceCount,
      totalVehicles,
      workshopVehicles: workshopVehicles.length,
      motVehicles: motVehicles.length,
      bodyshopVehicles: bodyshopVehicles.length,
      tyresVehicles: tyresVehicles.length,
      workshopRevenue,
      motRevenue,
      bodyshopRevenue,
      tyresRevenue,
      averageInvoice: invoiceCount > 0 ? totalRevenue / invoiceCount : 0
    };
  }, [activeVehicles, invoices, isInPeriod, garageInfo]);

  // Calculate financial data for Sales - using same logic as SalesReport
  const salesData = useMemo(() => {
    const salesVehicles = activeVehicles.filter(v => v.status === 'Sales');

    // Filter by period - when "Sales Only" is selected, show ALL sales vehicles (like SalesReport)
    // Otherwise, filter by period for combined/all views
    const periodSalesVehicles = category === 'sales' 
      ? salesVehicles // Show all sales vehicles when "Sales Only" is selected (matches SalesReport)
      : salesVehicles.filter(v => {
          // For period-based filtering, include vehicles that were:
          // 1. Collected/sold in the period, OR
          // 2. Added to stock in the period
          const collectionDate = v.collectionDate;
          const dropOffDate = v.dropOffDate || v.receivedDate;
          
          if (collectionDate && isInPeriod(collectionDate)) {
            return true;
          }
          if (dropOffDate && isInPeriod(dropOffDate)) {
            return true;
          }
          return false;
        });

    // Calculate totals using same logic as SalesReport
    let totalRevenue = 0;
    let totalCost = 0;
    let totalPrepCost = 0;
    let totalWarranty = 0;
    let totalMisc = 0;
    let salesMade = 0;
    let depositsTaken = 0;

    periodSalesVehicles.forEach((veh) => {
      const cost = veh.cost || 0;
      const prepCost = veh.prepCost || 0;
      const warranty = veh.warranty || 0;
      const misc = veh.misc || 0;
      const salePrice = veh.salePrice || 0;

      // Check both dealStage and salesStage for compatibility
      let dealStage = veh.dealStage;
      if (!dealStage && veh.salesStage) {
        if (veh.salesStage === 'deposit') dealStage = 'depositTaken';
        else if (veh.salesStage === 'prep') dealStage = 'inPrep';
        else if (veh.salesStage === 'forSale') dealStage = 'forSale';
        else if (veh.salesStage === 'sold') dealStage = 'sold';
      }
      if (!dealStage) dealStage = 'inStock';

      const isSold = dealStage === 'sold' || dealStage === 'collected' || veh.salesStage === 'sold';
      const hasDeposit = dealStage === 'depositTaken' || veh.salesStage === 'deposit' || isSold;

      // For revenue, use salePrice (same as SalesReport)
      totalRevenue += salePrice;
      totalCost += cost;
      totalPrepCost += prepCost;
      totalWarranty += warranty;
      totalMisc += misc;

      if (isSold) salesMade++;
      if (hasDeposit) depositsTaken++;
    });

    const totalVehicleCost = totalCost + totalPrepCost + totalWarranty + totalMisc;
    const totalProfit = totalRevenue - totalVehicleCost;
    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    // Breakdown by deal stage
    const soldVehicles = periodSalesVehicles.filter(v => {
      let dealStage = v.dealStage;
      if (!dealStage && v.salesStage) {
        if (v.salesStage === 'sold') return true;
        if (v.salesStage === 'deposit') dealStage = 'depositTaken';
        else if (v.salesStage === 'prep') dealStage = 'inPrep';
        else if (v.salesStage === 'forSale') dealStage = 'forSale';
      }
      return dealStage === 'sold' || dealStage === 'collected';
    });

    const depositVehicles = periodSalesVehicles.filter(v => {
      let dealStage = v.dealStage;
      if (!dealStage && v.salesStage) {
        if (v.salesStage === 'deposit') return true;
        if (v.salesStage === 'prep') dealStage = 'inPrep';
        else if (v.salesStage === 'forSale') dealStage = 'forSale';
        else if (v.salesStage === 'sold') dealStage = 'sold';
      }
      return dealStage === 'depositTaken';
    });

    const soldRevenue = soldVehicles.reduce((sum, veh) => sum + (veh.salePrice || 0), 0);
    const depositRevenue = depositVehicles.reduce((sum, veh) => sum + (veh.salePrice || 0), 0);

    // Get invoices for sales vehicles in period
    const salesInvoices = invoices.filter(inv => {
      const vehicle = periodSalesVehicles.find(v => v.reg === inv.reg);
      return vehicle && isInPeriod(inv.date);
    });

    return {
      totalRevenue,
      totalCost: totalVehicleCost,
      totalProfit,
      profitMargin,
      totalVehicles: periodSalesVehicles.length,
      soldVehicles: soldVehicles.length,
      depositVehicles: depositVehicles.length,
      soldRevenue,
      depositRevenue,
      salesMade,
      depositsTaken,
      invoiceCount: salesInvoices.length,
      averageSale: periodSalesVehicles.length > 0 ? totalRevenue / periodSalesVehicles.length : 0,
      // Additional breakdown like SalesReport
      totalPrepCost,
      totalWarranty,
      totalMisc
    };
  }, [activeVehicles, invoices, isInPeriod, category]);

  // Combined totals
  const combinedData = useMemo(() => {
    return {
      totalRevenue: garageData.totalRevenue + salesData.totalRevenue,
      garageRevenue: garageData.totalRevenue,
      salesRevenue: salesData.totalRevenue,
      totalVehicles: garageData.totalVehicles + salesData.totalVehicles,
      totalInvoices: garageData.invoiceCount + salesData.invoiceCount
    };
  }, [garageData, salesData]);

  // Period labels
  const periodLabels = {
    week: 'This Week',
    month: 'This Month',
    year: 'This Year'
  };

  // Render Garage section
  const renderGarageSection = () => (
    <div className="finance-section" style={{ marginBottom: '32px' }}>
      <h3 style={{ 
        fontSize: '20px', 
        fontWeight: '600', 
        marginBottom: '20px',
        color: 'var(--text-dark)'
      }}>
        Garage (Workshop / MOT / Bodyshop / Tyres)
      </h3>

      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          padding: '20px',
          background: 'var(--light-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Total Revenue
          </div>
          <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-dark)' }}>
            {formatCurrency(garageData.totalRevenue)}
          </div>
        </div>

        <div style={{
          padding: '20px',
          background: 'var(--light-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Total Vehicles
          </div>
          <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-dark)' }}>
            {garageData.totalVehicles}
          </div>
        </div>

        <div style={{
          padding: '20px',
          background: 'var(--light-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Invoices
          </div>
          <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-dark)' }}>
            {garageData.invoiceCount}
          </div>
        </div>

        <div style={{
          padding: '20px',
          background: 'var(--light-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Average Invoice
          </div>
          <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-dark)' }}>
            {formatCurrency(garageData.averageInvoice)}
          </div>
        </div>
      </div>

      {/* Breakdown by Service Type */}
      <div style={{
        padding: '20px',
        background: 'var(--light-bg)',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        marginBottom: '24px'
      }}>
        <h4 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '16px',
          color: 'var(--text-dark)'
        }}>
          Breakdown by Service Type
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {isStageEnabled(garageInfo, 'Workshop') && (
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Workshop</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(garageData.workshopRevenue)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              {garageData.workshopVehicles} vehicles
            </div>
          </div>
          )}
          {isStageEnabled(garageInfo, 'MOT') && (
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>MOT</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(garageData.motRevenue)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              {garageData.motVehicles} vehicles
            </div>
          </div>
          )}
          {isStageEnabled(garageInfo, 'Bodyshop') && (
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Bodyshop</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(garageData.bodyshopRevenue)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              {garageData.bodyshopVehicles} vehicles
            </div>
          </div>
          )}
          {isStageEnabled(garageInfo, 'Tyres') && (
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Tyres</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(garageData.tyresRevenue)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              {garageData.tyresVehicles} vehicles
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Financial Details */}
      <div style={{
        padding: '20px',
        background: 'var(--light-bg)',
        borderRadius: '12px',
        border: '1px solid var(--border)'
      }}>
        <h4 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '16px',
          color: 'var(--text-dark)'
        }}>
          Financial Details
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Subtotal</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(garageData.totalSubtotal)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>VAT (20%)</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(garageData.totalVAT)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Total</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(garageData.totalRevenue)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Sales section
  const renderSalesSection = () => (
    <div className="finance-section" style={{ marginBottom: '32px' }}>
      <h3 style={{ 
        fontSize: '20px', 
        fontWeight: '600', 
        marginBottom: '20px',
        color: 'var(--text-dark)'
      }}>
        Sales
      </h3>

      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          padding: '20px',
          background: 'var(--light-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Total Revenue
          </div>
          <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-dark)' }}>
            {formatCurrency(salesData.totalRevenue)}
          </div>
        </div>

        <div style={{
          padding: '20px',
          background: 'var(--light-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Total Profit
          </div>
          <div style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: salesData.totalProfit >= 0 ? 'var(--success)' : 'var(--error)'
          }}>
            {formatCurrency(salesData.totalProfit)}
          </div>
        </div>

        <div style={{
          padding: '20px',
          background: 'var(--light-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Total Vehicles
          </div>
          <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-dark)' }}>
            {salesData.totalVehicles}
          </div>
        </div>

        <div style={{
          padding: '20px',
          background: 'var(--light-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Profit Margin
          </div>
          <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-dark)' }}>
            {salesData.profitMargin.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Breakdown by Deal Stage */}
      <div style={{
        padding: '20px',
        background: 'var(--light-bg)',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        marginBottom: '24px'
      }}>
        <h4 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '16px',
          color: 'var(--text-dark)'
        }}>
          Breakdown by Deal Stage
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Sold</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(salesData.soldRevenue)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              {salesData.soldVehicles} vehicles
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Deposit Taken</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(salesData.depositRevenue)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              {salesData.depositVehicles} vehicles
            </div>
          </div>
        </div>
      </div>

      {/* Financial Details */}
      <div style={{
        padding: '20px',
        background: 'var(--light-bg)',
        borderRadius: '12px',
        border: '1px solid var(--border)'
      }}>
        <h4 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '16px',
          color: 'var(--text-dark)'
        }}>
          Financial Details
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Revenue</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(salesData.totalRevenue)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Total Cost</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(salesData.totalCost)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Profit</div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: salesData.totalProfit >= 0 ? 'var(--success)' : 'var(--error)'
            }}>
              {formatCurrency(salesData.totalProfit)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Average Sale</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-dark)' }}>
              {formatCurrency(salesData.averageSale)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="vehicle-inner finance-page">
      <div className="vehicle-header">
        <div>
          <h2>Finance</h2>
          <p className="vehicle-subtitle">Complete financial breakdown by category and period</p>
        </div>
      </div>

      <div className="vehicle-content">
        {/* Period Filter */}
        <div className="finance-period-filters" style={{ 
          display: 'flex', 
          gap: '12px', 
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          <button
            type="button"
            className={`report-filter-btn ${period === 'week' ? 'active' : ''}`}
            onClick={() => setPeriod('week')}
          >
            Week
          </button>
          <button
            type="button"
            className={`report-filter-btn ${period === 'month' ? 'active' : ''}`}
            onClick={() => setPeriod('month')}
          >
            Month
          </button>
          <button
            type="button"
            className={`report-filter-btn ${period === 'year' ? 'active' : ''}`}
            onClick={() => setPeriod('year')}
          >
            Year
          </button>
        </div>

        {/* Overall Summary */}
        <div className="finance-summary-hero" style={{
          padding: '24px',
          background: 'linear-gradient(135deg, var(--primary) 0%, #7aa7ff 100%)',
          borderRadius: '12px',
          marginBottom: '32px',
          color: '#fff'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
            {periodLabels[period]} - Total Revenue
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '16px' }}>
            {formatCurrency(combinedData.totalRevenue)}
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '16px',
            marginTop: '16px'
          }}>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Garage Revenue</div>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {formatCurrency(combinedData.garageRevenue)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Sales Revenue</div>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {formatCurrency(combinedData.salesRevenue)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Total Vehicles</div>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {combinedData.totalVehicles}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Total Invoices</div>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {combinedData.totalInvoices}
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="finance-category-filters" style={{ 
          display: 'flex', 
          gap: '12px', 
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          <button
            type="button"
            className={`report-filter-btn ${category === 'all' ? 'active' : ''}`}
            onClick={() => setCategory('all')}
          >
            All
          </button>
          <button
            type="button"
            className={`report-filter-btn ${category === 'garage' ? 'active' : ''}`}
            onClick={() => setCategory('garage')}
          >
            Garage Only
          </button>
          {isStageEnabled(garageInfo, 'Sales') && (
          <button
            type="button"
            className={`report-filter-btn ${category === 'sales' ? 'active' : ''}`}
            onClick={() => setCategory('sales')}
          >
            Sales Only
          </button>
          )}
        </div>

        {/* Render sections based on category filter */}
        {(category === 'all' || category === 'garage') && renderGarageSection()}
        {isStageEnabled(garageInfo, 'Sales') && (category === 'all' || category === 'sales') && renderSalesSection()}
      </div>
    </div>
  );
};

export default Finance;
